import { EditableGridStateColDef } from "../types";
import { gridSortModelSelector } from "@mui/x-data-grid-pro";
import { SortEnumType } from "graphqlBase/types";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";

/**
 * Builds the GraphQL `order` array from a MUI sort model, combined with the
 * base `variables.order` fallback.
 *
 * Priority: sort-model items (mapped via `column.remoteOrder` when available,
 * otherwise `{ [field]: direction }`) come first; `variables.order` is
 * appended as a tiebreaker / default sort.
 */
export const buildOrderFromModel = <E extends keyof EntityMappings>({
  sortModel,
  variables,
  columns = [],
}: {
  sortModel: ReturnType<typeof gridSortModelSelector>;
  variables: { order?: EntityMappings[E]["sort"] };
  columns?: EditableGridStateColDef[];
}): EntityMappings[E]["sort"] => {
  const fromModel = sortModel.flatMap((item) => {
    if (!item.sort) return [];
    const direction = item.sort.toUpperCase() as SortEnumType;
    const column = columns.find((c) => c.field === item.field);
    if (column?.remoteOrder) {
      const mapped = column.remoteOrder(direction) as EntityMappings[E]["sort"];
      return Array.isArray(mapped) ? mapped : [mapped];
    }
    return [{ [item.field]: direction } as EntityMappings[E]["sort"]];
  });

  const base = variables?.order ?? [];
  const baseArr = (Array.isArray(base) ? base : [base]) as EntityMappings[E]["sort"][];

  return [...fromModel, ...baseArr] as EntityMappings[E]["sort"];
};

export const buildSortFromFilters = <E extends keyof EntityMappings>({
  columns,
  sortModel,
  variables,
}: {
  columns: EditableGridStateColDef[];
  sortModel: ReturnType<typeof gridSortModelSelector>;
  variables: { order?: EntityMappings[E]["sort"] };
}) => {
  const sort = sortModel.reduce<EntityMappings[E]["sort"]>((sorts, elem) => {
    const column = columns.find((c) => c.field === elem.field);
    if (!column?.remoteOrder || !elem.sort) return sorts;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { ...sorts, ...(column.remoteOrder(elem.sort.toUpperCase() as SortEnumType) as any) };
  }, {});

  const sortInPre = variables?.order ?? {};
  const sortIn = Array.isArray(sortInPre) ? sortInPre : [sortInPre];

  const order = Object.entries(sort)
    .reduce<EntityMappings[E]["sort"][]>((sorts, [key, value]) => {
      return sorts.concat({ [key]: value });
    }, [])
    .concat(...sortIn);

  return order;
};
